import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { FaKey, FaCog, FaLifeRing, FaInfoCircle } from "react-icons/fa";

export default function HelpSettingsPage({ apiBase = process.env.REACT_APP_API_URL }) {

    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };

    const [buildVersion, setBuildVersion] = useState("");
    const [version, setVersion] = useState("");

    const [defaultTab, setDefaultTab] = useState("PROFILER");
    const [savingTab, setSavingTab] = useState(false);
    const [saveMsg, setSaveMsg] = useState(null);

    const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
    const [changingPw, setChangingPw] = useState(false);
    const [pwMsg, setPwMsg] = useState(null);

    const client = useMemo(() => axios.create({ baseURL: apiBase || "/" }), [apiBase]);

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const meta = await client.get("/meta").then(r => r.data).catch(() => ({ version: "6", buildVersion: "Profiler V8 - CURRENT RELEASE" }));
                if (!mounted) return;
                setVersion(`${meta?.version ?? "6"}`);
                setBuildVersion(meta?.buildVersion ?? "Profiler V8 - CURRENT RELEASE");

                const prefs = await client.get("/me/preferences").then(r => r.data).catch(() => ({ defaultTab: "PROFILER" }));
                if (!mounted) return;
                setDefaultTab(prefs?.defaultTab ?? "PROFILER");
            } catch {}
        })();
        return () => { mounted = false; };
    }, [client]);

    async function handleSaveDefaultTab() {
       /* setSavingTab(true);
        setSaveMsg(null);
        try {
            await client.put("/me/preferences", { defaultTab });
            setSaveMsg({ type: "success", text: "Default tab updated." });
        } catch {
            setSaveMsg({ type: "error", text: "Couldn't save preference. Please try again." });
        } finally {
            setSavingTab(false);
        }*/
    }

    async function handleChangePassword() {
        setPwMsg(null);
        if (!pw.current || !pw.next || !pw.confirm) return setPwMsg({ type: "error", text: "Please fill in all fields." });
        if (pw.next !== pw.confirm) return setPwMsg({ type: "error", text: "New password and confirmation do not match." });
        if (pw.next.length < 8) return setPwMsg({ type: "error", text: "New password must be at least 8 characters." });

        setChangingPw(true);
        try {
            await client.post("/auth/change-password", { currentPassword: pw.current, newPassword: pw.next }, { headers });
            setPw({ current: "", next: "", confirm: "" });
            setPwMsg({ type: "success", text: "Password changed successfully." });
        } catch {
            setPwMsg({ type: "error", text: "Couldn't change password. Check your current password and try again." });
        } finally {
            setChangingPw(false);
        }
    }

    function Collapsible({ title, summary, children, defaultOpen = false }) {
        const [open, setOpen] = useState(defaultOpen);
        return (
            <div className="rounded-xl border p-3">
                <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <h5 className="text-sm font-semibold">{title}</h5>
                        {summary && <p className="text-xs text-gray-600 mt-0.5">{summary}</p>}
                    </div>
                    <button
                        onClick={() => setOpen(o => !o)}
                        className="text-xs font-medium text-[#25408f] underline shrink-0"
                    >
                        {open ? "View less" : "View more"}
                    </button>
                </div>
                {open && <div className="mt-2 text-xs leading-5">{children}</div>}
            </div>
        );
    }


    const showWhatsNew = ["4", "5", "6", "7", "8"].includes(`${version}`);

    return (
        <div className="min-h-[280px] h-[550px]  w-full min-w-0 overflow-y-auto p-4 space-y-4 text-sm">
            <div className="text-right text-[10px] text-gray-500 truncate">{buildVersion}</div>

            <h1 className="text-lg font-semibold text-[#25408f] flex items-center gap-2">
                <FaCog className="inline-block" /> Help & Settings
            </h1>
            <div className="border-t" />

            {/* Support */}
            <section className="space-y-3">
                <div className="flex items-center gap-2">
                    <FaLifeRing />
                    <h2 className="text-base font-semibold">Support</h2>
                </div>
                <div className="grid grid-cols-1 gap-3">
                    <AddressCard
                        title="3d Innovations"
                        lines={["288 Bishopsgate", "London, EC2M 4QP", "UK", "", "P: +44 20 3709 4335"]}
                        email={{ label: "profiler@3di-ltd.com", href: "mailto:profiler@3di-ltd.com" }}
                    />
                    <AddressCard
                        title="3d Innovations"
                        lines={["445 Park Avenue,", "New York, NY 10022", "USA", "", "P: +1 212-829-5531"]}
                        email={{ label: "profiler@3di-inc.com", href: "mailto:profiler@3di-inc.com" }}
                    />
                    <AddressCard
                        title="3d Innovations"
                        lines={["Beursplein 5,", "1012 JW Amsterdam", "Netherlands", "", "P: +31 20 578 25 71"]}
                        email={{ label: "profiler@3di-ltd.com", href: "mailto:profiler@3di-ltd.com" }}
                    />
                </div>
            </section>

            <div className="border-t" />

            {/* Settings: single-column for sidebar */}
            <section className="grid grid-cols-1 gap-4">
                {/* Change Password */}
                <div className="rounded-xl border p-4 shadow-sm">
                    <h3 className="text-base font-semibold flex items-center gap-2 text-[#25408f]">
                        <FaKey /> Change Password
                    </h3>
                    <div className="mt-3 space-y-2">
                        <LabelledInput
                            id="currentpassword"
                            label="Current Password"
                            type="password"
                            value={pw.current}
                            onChange={(e) => setPw((s) => ({ ...s, current: e.target.value }))}
                        />
                        <LabelledInput
                            id="newpassword"
                            label="New Password"
                            type="password"
                            value={pw.next}
                            onChange={(e) => setPw((s) => ({ ...s, next: e.target.value }))}
                        />
                        <LabelledInput
                            id="confirmpassword"
                            label="Confirm Password"
                            type="password"
                            value={pw.confirm}
                            onChange={(e) => setPw((s) => ({ ...s, confirm: e.target.value }))}
                        />
                        {pwMsg && (
                            <div className={`text-xs ${pwMsg.type === "success" ? "text-green-600" : "text-red-600"}`}>{pwMsg.text}</div>
                        )}
                        <button
                            onClick={handleChangePassword}
                            disabled={changingPw}
                            className="w-full inline-flex justify-center items-center rounded-md bg-[#25408f] px-3 py-2 text-white font-medium hover:opacity-90 disabled:opacity-50"
                        >
                            {changingPw ? "Changing…" : "Change password"}
                        </button>
                    </div>
                </div>

                {/* Default tab */}
                <div className="rounded-xl border p-4 shadow-sm">
                    <h3 className="text-base font-semibold flex items-center gap-2 text-[#25408f]">
                        <FaCog /> Default Profiler Tab
                    </h3>
                    <div className="mt-3 space-y-2">
                        <select
                            id="mainscreen"
                            className="w-full rounded-md border p-2"
                            value={defaultTab}
                            onChange={(e) => setDefaultTab(e.target.value)}
                        >
                            <option value="PROFILER">Profiler</option>
                            {/* to add once the other tabs are implemented
                            <option value="XTVM">XTVM</option>
                            <option value="ALT_DATA">Alt Data</option>*/}
                        </select>
                        {saveMsg && (
                            <div className={`text-xs ${saveMsg.type === "success" ? "text-green-600" : "text-red-600"}`}>
                                {saveMsg.text}
                            </div>
                        )}
                        <button
                            onClick={handleSaveDefaultTab}
                            disabled={savingTab}
                            className="w-full inline-flex justify-center items-center rounded-md bg-[#25408f] px-3 py-2 text-white font-medium hover:opacity-90 disabled:opacity-50"
                        >
                            {savingTab ? "Saving…" : "Change Default Tab"}
                        </button>
                    </div>
                </div>
            </section>

            {showWhatsNew && (
                <section className="space-y-3 break-words">
                    <div className="flex items-center gap-2">
                        <FaInfoCircle />
                        <h2 className="text-base font-semibold">What&apos;s New?</h2>
                    </div>

                    {/* V8 — CURRENT RELEASE (always expanded) */}
                    <h5 className="text-base font-semibold text-[#25408f] font-semibold mt-1">Profiler V8 — CURRENT RELEASE</h5>
                    <ul className="list-disc ml-5 space-y-1 text-xs leading-5">
                        <li><strong>New UI</strong> for faster navigation and a cleaner, compact layout.</li>
                    </ul>

                    {/* V7 — 3di Indices & Benchmarks Module */}
                    <Collapsible
                        title="Profiler V7 — 3di Indices & Benchmarks Module"
                        summary="First-of-its-kind platform focused on Index Providers, Families, Indices & related indices with powerful metadata search."
                    >
                        <ul className="list-disc ml-5 space-y-1">
                            <li>
                                <strong>Deep coverage:</strong> 150,000+ indices, 2,000+ index families, 250+ index providers.
                            </li>
                            <li>
                                <strong>Search across:</strong> Indices, Tickers, Index Families, Index Providers.
                            </li>
                            <li>
                                <strong>Screening features include:</strong>
                                <ul className="list-disc ml-5 mt-1">
                                    <li>Asset Class</li>
                                    <li>Region</li>
                                    <li>Theme</li>
                                    <li>Market Quality</li>
                                    <li>Geography</li>
                                    <li>Sector</li>
                                    <li>Frequency</li>
                                    <li>Currency</li>
                                    <li>Weight Type</li>
                                    <li>…plus Custom searching.</li>
                                </ul>
                            </li>
                            <li>
                                <strong>Descriptive information</strong> at Index, Family, and Provider levels.
                            </li>
                            <li>
                                <strong>Lexicon fields</strong> for all Index records:
                                <ul className="list-disc ml-5 mt-1">
                                    <li>Index Name</li>
                                    <li>Index Family</li>
                                    <li>Ticker Symbol</li>
                                    <li>Launch Date</li>
                                    <li>Calculation Currency</li>
                                    <li>Weighting Methodology</li>
                                    <li>Index Description</li>
                                    <li>Index Geography</li>
                                    <li>Index Theme</li>
                                    <li>Asset Class</li>
                                    <li>Investment Strategy</li>
                                </ul>
                            </li>
                            <li><strong>Related Indices</strong> auto-generated for all Index records.</li>
                            <li>
                                <strong>Licensing & Commercial Policy</strong> and <strong>Rights Management</strong> intelligence for Index Families.
                            </li>
                            <li>
                                <strong>Access</strong> to 3di’s Research & Analysis team for bespoke support and advice.
                            </li>
                        </ul>
                    </Collapsible>

                    {/* V6.8 */}
                    <Collapsible
                        title="Profiler V6.8"
                        summary="ESG enrichment, dashboard, new searches, and compare features."
                    >
                        <ul className="list-disc ml-5 space-y-1">
                            <li>150+ new ESG-specific search categories across 300+ ESG products & vendors.</li>
                            <li>
                                ESG Dashboard in Alt Data (no extra charge):
                                <ul className="list-disc ml-5 mt-1">
                                    <li>Granular ESG taxonomy navigation.</li>
                                    <li>Curated Searches by primary ESG factors.</li>
                                    <li>Custom Search across all 150 ESG categories.</li>
                                </ul>
                            </li>
                            <li>
                                New ESG searches:
                                <ul className="list-disc ml-5 mt-1">
                                    <li>ESG Data/Functionality Coverage</li>
                                    <li>ESG Vendor Category</li>
                                </ul>
                            </li>
                            <li>Built-in ESG Compare Products for quick comparative analysis.</li>
                        </ul>
                    </Collapsible>

                    {/* V6.5 */}
                    <Collapsible
                        title="Profiler V6.5"
                        summary="XTVM v3; enhanced Policies & Documentation; expanded categories; new Exchange Alerts tab; repository upgrades."
                    >
                        <ul className="list-disc ml-5 space-y-1">
                            <li>XTVM Version 3 released.</li>
                            <li>
                                ETS “Policies & Documentation” enhanced; merged “Reporting & Auditing”:
                                <ul className="list-disc ml-5 mt-1">
                                    <li>Expandable categories; clearer sort order.</li>
                                    <li>Exec Summaries and Transcripts separated; transcripts link to source.</li>
                                    <li>Policy categories show Exchange Alert flags.</li>
                                    <li>Future vs Current policies side-by-side with materiality indicators.</li>
                                </ul>
                            </li>
                            <li>
                                Expanded policy categories (60+):
                                <ul className="list-disc ml-5 mt-1">
                                    <li>Definitions</li>
                                    <li>Derived Data – Index Creation (consumers & calculation agents)</li>
                                    <li>Historical Data</li>
                                    <li>Professional / Non-Professional Users</li>
                                    <li>Display & Distribution of Own Share Price</li>
                                    <li>Market Data Promotions & Discounts</li>
                                    <li>Market Data Support & Admin</li>
                                    <li>Termination for Non-Payment / Material Breach</li>
                                    <li>Non-Market Data: Warranties, Liability, IP, Jurisdiction</li>
                                </ul>
                            </li>
                            <li>
                                “Simplified” categories:
                                <ul className="list-disc ml-5 mt-1">
                                    <li>Non-Display – Simplified Unit of Count (3di’s simplified counts)</li>
                                    <li>Display Devices – Simplified Unit of Count (3di’s simplified counts)</li>
                                    <li>Cancellation & Termination — Notice Period (# of days)</li>
                                </ul>
                            </li>
                            <li>
                                10 renamed categories (clarity):
                                <ul className="list-disc ml-5 mt-1">
                                    <li>Distribution Policies → Internal Distribution Policies</li>
                                    <li>Subsidiaries → Subsidiaries & Affiliates</li>
                                    <li>Derived Data → Derived Data & Creation of Data Products</li>
                                    <li>Additional Distribution Channels → Distribution via Facilitators/White-Label/API</li>
                                    <li>Snap Shot – User Fees → Snapshot & Per Quote</li>
                                    <li>Hand Held Devices – User Fees → Hand Held Devices</li>
                                    <li>Internet Pricing – User Fees → Unrestricted Public Display</li>
                                    <li>Non-Fee Liable Devices → Fee Liability for Other Usage</li>
                                    <li>Market Data Controls → Market Data Controls & Record Keeping</li>
                                    <li>Terminations → Cancellation of Agreement</li>
                                </ul>
                            </li>
                            <li>
                                Document Repository:
                                <ul className="list-disc ml-5 mt-1">
                                    <li>Sortable table; hide “Superseded & Retired”.</li>
                                    <li>Date cols: Published, Announced, Effective, Expiry.</li>
                                </ul>
                            </li>
                            <li>
                                New “Exchange Alerts” tab:
                                <ul className="list-disc ml-5 mt-1">
                                    <li>Filter/search; sortable; source docs linked.</li>
                                    <li>Current & future pricing shown under change description.</li>
                                </ul>
                            </li>
                            <li>
                                Enhanced Exchange Alerts:
                                <ul className="list-disc ml-5 mt-1">
                                    <li>Tag alerts to exchanges & categories.</li>
                                    <li>Pricing changes: expanded categories; per-category granularity.</li>
                                    <li>Links to XTVM Document Repository and Exchange Direct Pricing.</li>
                                    <li>Tag sub-exchange where applicable.</li>
                                </ul>
                            </li>
                            <li>Preferences & Alerts now include Exchange Alerts; “My Profiler” updated.</li>
                            <li>
                                XTVM “Added Value” moved under Search:
                                <ul className="list-disc ml-5 mt-1">
                                    <li>Exchange Alerts</li>
                                    <li>Exchange Listing (MIC)</li>
                                    <li>Vendor Entitlement Codes</li>
                                </ul>
                            </li>
                            <li>
                                Policy search export to PDF:
                                <ul className="list-disc ml-5 mt-1">
                                    <li>Executive Summaries only</li>
                                    <li>Transcripts only</li>
                                    <li>Both (as per V5 & V6)</li>
                                </ul>
                            </li>
                            <li>Other: Alt Data can be set as default focus after login.</li>
                        </ul>
                    </Collapsible>

                    {/* V6 */}
                    <Collapsible
                        title="Profiler V6"
                        summary="Alt-Data module launch, tagging, daily news, and Alt-Data alert triggers."
                    >
                        <ul className="list-disc ml-5 space-y-1">
                            <li>Alternative Data Module (Alt-Data) released.</li>
                            <li>Features: extended markets; tagging across Content, Data Source, Technology, Functionality.</li>
                            <li>Daily Alt-Data news; enhanced alerts with Alt-Data triggers.</li>
                            <li>
                                Product types:
                                <ul className="list-disc ml-5 mt-1">
                                    <li>Corporate / Market Data</li>
                                    <li>Environmental Data</li>
                                    <li>Government Data</li>
                                    <li>News & Views Data</li>
                                    <li>Sensors — Geolocation / Satellite</li>
                                    <li>Sentiment Data</li>
                                    <li>Transaction Data</li>
                                    <li>Web/App/Mobile/Social Media Data</li>
                                </ul>
                            </li>
                        </ul>
                    </Collapsible>

                    {/* V5.1 & V5 */}
                    <Collapsible
                        title="Profiler V5.1 & V5"
                        summary="V5.1: XTVM stubs & dashboard. V5: My Alerts, My Profiler, Default Tab."
                    >
                        <h6 className="font-semibold mt-1">Profiler V5.1</h6>
                        <ul className="list-disc ml-5 space-y-1">
                            <li>“Stubs” for upcoming exchanges/venues in XTVM (watch with My Alerts).</li>
                            <li>New XTVM launch dashboard: last six Exchange Alerts.</li>
                        </ul>
                        <h6 className="font-semibold mt-2">Profiler V5</h6>
                        <ul className="list-disc ml-5 space-y-1">
                            <li>Preferences & Alerts (My Alerts) across vendor/product/exchange tearsheets; email or in-app.</li>
                            <li>My Profiler: triggered alerts + saved searches (rename/modify).</li>
                            <li>Default Profiler Tab setting added (also under Help & Settings).</li>
                        </ul>
                    </Collapsible>

                    {/* V4.5 */}
                    <Collapsible
                        title="Profiler V4.5"
                        summary="Adds What’s New, Commercial filter, Document Repository tab, pricing/export fixes."
                    >
                        <ul className="list-disc ml-5 space-y-1">
                            <li>“What’s New?” added to Help.</li>
                            <li>Commercial filter in XTVM Relationship (who sets policy/pricing).</li>
                            <li>Exchange Tearsheet: Document Repository tab with quick list & targeted filtering.</li>
                            <li>Pricing exports: Excel-only (vendor codes & direct pricing tabs); removed pricing from PDF export.</li>
                            <li>Pricing: End Date column; fixed Previous/Current/Future filters.</li>
                            <li>Exchange Alert filter fixed (recently live vs upcoming changes).</li>
                            <li>Vendor Tree: “Indices & Benchmarks” filter + matching search options.</li>
                            <li>Product Category: added “Alternative Data”.</li>
                            <li>Minor bug fix on redirect from tree view.</li>
                        </ul>
                    </Collapsible>
                </section>
            )}


        </div>
    );
}

function LabelledInput({ id, label, type = "text", value, onChange }) {
    return (
        <label htmlFor={id} className="block">
            <span className="block text-xs font-medium text-gray-700 mb-1">{label}</span>
            <input
                id={id}
                type={type}
                value={value}
                onChange={onChange}
                className="w-full rounded-md border p-2 focus:outline-none focus:ring-2 focus:ring-[#25408f]/40"
            />
        </label>
    );
}

function AddressCard({ title, lines, email }) {
    return (
        <div className="rounded-xl border p-4 shadow-sm">
            <h4 className="font-semibold text-[#25408f] text-sm">{title}</h4>
            <div className="mt-2 text-xs text-gray-700 space-y-0.5 break-words">
                {lines.map((l, i) => (
                    <div key={i}>{l}</div>
                ))}
                {email && (
                    <div className="pt-2">
                        Email:{" "}
                        <a className="text-[#25408f] underline break-all" href={email.href}>
                            {email.label}
                        </a>
                    </div>
                )}
            </div>
        </div>
    );
}
